import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {Box, CircularProgress, Stack, TextField} from "@mui/material";
import DialogueBox from "app/components/DialogueBox";
import {useEffect, useState} from "react";
import {useLocation, useParams} from "react-router-dom"; // Import necessary hooks
import {toast} from "react-toastify";
import {axios_tenant} from "../../../axios";
import AppContext from "./AppContext"; // Import the context created in step 1
import Breadcrumb from "./Breadcrumb";
import MainContentArea from "./MainContentArea";
import NewButton from "./NewButton";
import UploadStatusPopup from "./UploadStatusPopup";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";

function CollaborationSpace() {
    // State to store the folder contents
    const [folderContents, setFolderContents] = useState([]);
    const [clickDownload, setClickDownload] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [showIcons, setShowIcons] = useState(false);
    const [clickDelete, setClickDelete] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [confirmDelete,setConfirmDelete] = useState(false);
    const [uploadPopups, setUploadPopups] = useState([]);
    const [appData, setAppData] = useState({
        serverDomain: `${process.env.REACT_APP_API_BASE_URL}/tenantAPI/collaboration` // Replace with the actual server domain
    });
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    // Use React Router's useParams and useLocation to extract folder IDs from the route
    let folderIds = useParams();
    folderIds = folderIds["*"];
    const location = useLocation();

    useEffect(()=>{
        setSearchText("");
    },[location.pathname])

    // Function to fetch folder contents from the server
    const fetchFolderContents = async () => {
        setLoading(true);
        const path = location.pathname;
        try {
            const isSharedPath = path.includes("/collaboration-space/shared/hash");
            if (path.includes("/collaboration-space/shared/hash")) {
                const pathList = path.split("/");
                const hash = pathList[pathList.indexOf("hash") + 1];
                const folderId = hash === pathList[pathList.length - 1] ? "" : pathList[pathList.length - 1];
                await fetchSharedFileContents(hash, folderId);
                setLoading(false);
            } else {
                const nestedFolderIds = path ? path.split("/") : [];
                const queryFolderId = nestedFolderIds.length > 3 ? nestedFolderIds[nestedFolderIds.length - 1] : "";
                let response;
                if (path === "/collaboration-space/shared") {
                    response = await axios_tenant.get(`/collaboration/getSharedData?folderId=${queryFolderId}`);
                } else if (path.includes("/collaboration-space/workspace")){
                    response = await axios_tenant.get(`/collaboration/getFolderContents?workspace=true&folderId=${queryFolderId}`);
                }else {
                    response = await axios_tenant.get(`/collaboration/getFolderContents?workspace=false&folderId=${queryFolderId}`);
                }

                if (response.status === 403) {
                    // Redirect to the specified path
                    const data = await response.data;

                    window.location.href = data.redirectPath;
                }
                if (!response.data) {
                    setLoading(false);
                    throw new Error("Failed to fetch folder contents");
                }

                const data = await response.data;
                setFolderContents(data.folderContents);
                setAppData(prevData => ({ ...prevData, folderId: data.folderId, breadcrumb: data.breadcrumb }));
                setLoading(false);
            }
        } catch (error) {

        }
    };

    const fetchSharedFileContents = async (hash, folderId) => {
        try {
            const response = await axios_tenant.get(`/collaboration/getSharedFile?hash=${hash}&folderId=${folderId}`);
            if (!response.data) {
                throw new Error("Failed to fetch shared file contents");
            }

            const data = await response.data;

            if (!data.exists) {
                toast.error(
                    "The file you are trying to access no longer exists."
                );

                return;
            }

            setFolderContents(data.folderContents);
            setAppData(prevData => ({ ...prevData, folderId: data.folderId, breadcrumb: data.breadcrumb }));
        } catch (error) {
        }
    };

    // Fetch folder contents when the component mounts or when the route changes
    useEffect(() => {
        fetchFolderContents();
    }, [location]); // Listen to location changes

    const filterFolderContents = folderContents?.filter(item => {
        if (item.folderName) {
            return item.folderName.toLowerCase().includes(searchText.toLowerCase());
        }
        if (item.fileName) {
            return item.fileName.toLowerCase().includes(searchText.toLowerCase());
        }
    });

    const handleDownload = selectedIds => {
        setClickDownload(false);
        if (selectedIds.length > 0) {
            setDownloadLoading(true);

            axios_tenant
                .get(`/collaboration/downloadMultiple`, {
                    params: {
                        selectedIdString: JSON.stringify(selectedIds)
                    },
                    responseType: "arraybuffer"
                })
                .then(res => {
                    setLoading(false);
                    setDownloadLoading(false);
                    const blob = new Blob([res.data], { type: "application/zip" });
                    const url = window.URL.createObjectURL(blob);

                    const a = document.createElement("a");
                    a.style.display = "none";
                    a.href = url;
                    a.download = res.headers["content-disposition"]?.split("filename=")[1];
                    document.body.appendChild(a);

                    a.click();

                    toast.success("Selected file(s) downloaded");
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                })
                .catch(err => {
                    setDownloadLoading(false);
                    toast.error("Download failed");
                });
        }
    };

    const handleDelete = selectedIds => {
        setClickDelete(false);
        setConfirmDelete(false);

        if (selectedIds.length > 0) {
            setDeleteLoading(true);

            axios_tenant
                .delete(`/collaboration/deleteMultiple`, {
                    params: {
                        selectedIdString: JSON.stringify(selectedIds)
                    }
                })
                .then(res => {
                    setDeleteLoading(false);
                    fetchFolderContents();
                    toast.success("Selected records deleted successfully");
                })
                .catch(err => {
                    // setLoading(false);
                    setDeleteLoading(false);
                    setConfirmDelete(false);
                    toast.error("Deleting records failed");
                });
        }
    };

    return (
        <AppContext.Provider value={appData}>
            <div>
                {/* Header Section */}
                <div>
                    <TextField
                        placeholder="Search"
                        size="small"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        inputProps={{ "aria-label": "Search" }}
                        sx={{
                            width: "40%",

                            "& .MuiOutlinedInput-root": {
                                height: 40,
                                borderRadius: "999px",
                                backgroundColor: "#fff",

                                "& fieldset": {
                                    borderColor: "#D9D9D9",
                                },

                                "&:hover fieldset": {
                                    borderColor: "#D9D9D9",
                                },

                                "&.Mui-focused fieldset": {
                                    borderColor: "#D9D9D9",
                                    borderWidth: "1px",
                                },
                            },

                            "& input": {
                                padding: "10px 0",
                                fontSize: "14px",
                            },

                            "& input::placeholder": {
                                color: "#A3A3A3", // slightly lighter placeholder
                                opacity: 1,
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment
                                    position="start"
                                    sx={{
                                        ml: 0.5,
                                        mr: 0.8,
                                    }}
                                >
                                    <SearchIcon
                                        sx={{
                                            color: "#6B7280",   // darker gray
                                            fontSize: 18,       // smaller icon
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {/* {downloadLoading ? (
                    <span>
                        <CircularProgress sx={{ color: "blue" }} /> Downloading...
                    </span>
                ) : (
                    showIcons && (
                        <span style={{ cursor: "pointer" }} onClick={showIcons ? () => setClickDownload(true) : () => {}}>
                            <FileDownloadIcon fontSize="large" /> Download
                        </span>
                    )
                )} */}

                    <br></br>
                    <br></br>
                    <Stack direction="row" alignItems="center" spacing={5}>
                        <Box display="flex" alignItems="center">
                            {appData.folderId && !location.pathname.includes("shared") && (
                                <NewButton
                                    fetchFolderContents={fetchFolderContents}
                                    setUploadPopups={setUploadPopups}
                                />
                            )}

                        </Box>
                        <Box sx={{ flexGrow: 1 }} />


                        {deleteLoading ? (
                            <span>
                                <CircularProgress sx={{ color: "blue" }} /> Deleting selected records...
                            </span>
                        ) : (
                            showIcons && !location.pathname.includes("shared") &&(
                                <span style={{ cursor: "pointer" }} onClick={showIcons ? () => setClickDelete(true) : () => {}}>
                                    <DeleteIcon fontSize="large" /> Delete
                                </span>
                            )
                        )}
                        {" "}
                        {downloadLoading ? (
                            <span>
                                <CircularProgress sx={{ color: "blue" }} /> Downloading...
                            </span>
                        ) : (
                            showIcons && (
                                <span style={{ cursor: "pointer" }} onClick={showIcons ? () => setClickDownload(true) : () => {}}>
                                    <FileDownloadIcon fontSize="large" /> Download
                                </span>
                            )
                        )}


                        <DialogueBox clickDelete={clickDelete} setClickDelete={setClickDelete} setConfirmDelete={setConfirmDelete} />
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={5}>
                        <Breadcrumb breadcrumb={appData.breadcrumb} />
                        <Box sx={{ flexGrow: 1 }} />
                        <div style={{fontStyle: "italic", fontSize: "80%",display:"flex"}}> *Maximum file size limit is 1 GB</div>
                    </Stack>
                </div>
                
                <Box
                    sx={{
                        position: "fixed",
                        bottom: 20,
                        right: 20,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        zIndex: 3000,
                    }}
                >
                    {uploadPopups.map((popup) => (
                        <UploadStatusPopup
                            key={popup.id}
                            open={true}
                            status={popup.status}
                            fileName={popup.fileName}
                        />
                    ))}
                </Box>
                <div className="main-content">{loading ? "Loading..." : <MainContentArea folderContents={filterFolderContents} fetchFolderContents={fetchFolderContents} handleDownload={handleDownload} clickDownload={clickDownload} setShowIcons={setShowIcons} confirmDelete={confirmDelete} handleDelete={handleDelete}/>}</div>
            </div>
        </AppContext.Provider>
    );
}

export default CollaborationSpace;